import { registerAuditLabels } from "@vixio/ui";
import type { StatusMap } from "@vixio/ui";

/** Tipos das linhas e das RPCs do módulo forms (ver modules/forms/README.md). */

export type FormKind = "diagnostico" | "pesquisa" | "contato" | "inscricao" | "avaliacao";
export type FormStatus = "draft" | "published" | "closed";
export type IdentityMode = "identified" | "optional" | "anonymous";
export type FieldType =
  | "short_text" | "long_text" | "single_choice" | "multi_choice" | "likert" | "nps" | "rating" | "date" | "email" | "phone" | "consent";

export const KIND_LABEL: Record<FormKind, string> = {
  diagnostico: "Diagnóstico",
  pesquisa: "Pesquisa",
  contato: "Contato",
  inscricao: "Inscrição",
  avaliacao: "Avaliação",
};

export const IDENTITY_LABEL: Record<IdentityMode, string> = {
  identified: "Identificado",
  optional: "Identificação opcional",
  anonymous: "Anônimo",
};

export const FORM_STATUS: StatusMap = {
  draft: { label: "rascunho", tone: "neutral" },
  published: { label: "publicado", tone: "ok" },
  closed: { label: "encerrado", tone: "warn" },
};

export const FIELD_LABEL: Record<FieldType, string> = {
  short_text: "Texto curto",
  long_text: "Texto longo",
  single_choice: "Escolha única",
  multi_choice: "Múltipla escolha",
  likert: "Escala (Likert)",
  nps: "NPS (0 a 10)",
  rating: "Nota (1 a 5)",
  date: "Data",
  email: "E-mail",
  phone: "Telefone",
  consent: "Aceite (sim/não)",
};

export const CHOICE_TYPES: FieldType[] = ["single_choice", "multi_choice"];
export const SCORED_TYPES: FieldType[] = ["likert", "rating", "nps"];
export const CONTACT_TYPES: FieldType[] = ["email", "phone"];

export interface FormRow {
  id: string;
  org_id: string;
  slug: string;
  kind: FormKind;
  title: string;
  status: FormStatus;
  identity_mode: IdentityMode;
  min_group_size: number;
  opens_at: string | null;
  closes_at: string | null;
  current_version_id: string | null;
  created_at: string;
  updated_at: string;
}

export type Option = string | { value: string; label?: string };

export interface FormItem {
  id: string;
  type: FieldType;
  text: string;
  required?: boolean;
  dimension?: string;
  weight?: number;
  reverse?: boolean;
  options?: Option[];
  max_length?: number;
  max_choices?: number;
  maps_to?: string;
}

export interface Dimension {
  key: string;
  label?: string;
  description?: string;
  weight?: number;
}

export interface FormDefinition {
  intro?: string;
  disclaimer?: string;
  scale?: { min: number; max: number; labels?: string[] };
  dimensions?: Dimension[];
  items: FormItem[];
  consents?: Array<{ purpose: string; required?: boolean | "if_identified" }>;
  identity_consent?: string;
  [k: string]: unknown;
}

export interface FormVersion {
  id: string;
  org_id: string;
  form_id: string;
  version: number;
  definition: FormDefinition;
  scoring: Record<string, unknown> | null;
  published_at: string | null;
  created_at: string;
  created_by: string | null;
}

export type Aggregate =
  | { suppressed: true; min_group_size: number }
  | {
      suppressed: false;
      n: number;
      min_group_size: number;
      score: number | null;
      dimensions: Array<{ key: string; label: string; score: number | null }>;
      items: Array<{ id: string; text: string; mean: number | null; n: number }>;
    };

export function optionLabel(o: Option): string {
  return typeof o === "string" ? o : o.label ?? o.value;
}
export function optionValue(o: Option): string {
  return typeof o === "string" ? o : o.value;
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "forms.create_form": {
      args: {
        p_org: string;
        p_slug: string;
        p_kind: FormKind;
        p_title: string;
        p_identity_mode?: IdentityMode;
        p_min_group_size?: number;
        p_opens_at?: string | null;
        p_closes_at?: string | null;
      };
      returns: string;
    };
    "forms.create_version": { args: { p_form: string; p_definition: FormDefinition; p_scoring?: Record<string, unknown> | null }; returns: string };
    "forms.publish_version": { args: { p_version: string }; returns: null };
    "forms.close_form": { args: { p_form: string }; returns: null };
    "forms.aggregate": { args: { p_form_id: string; p_group_key?: string | null }; returns: Aggregate };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "form.created": "Formulário criado",
  "form.version_created": "Versão do formulário criada",
  "form.version_published": "Versão publicada",
  "form.closed": "Formulário encerrado",
  "form.response_submitted": "Resposta recebida",
});
